import { BreadCrumbItem } from '@lib/common';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { useBreadcrumbFolders } from './use-breadcrumb-folders.hook';
import { DEFAULT_FOLDER_ID } from './use-resolved-folder-id.hook';
import { buildSpaceUrl, readSpaceFolderTrailFromQuery } from '../../../utils/space-url.util';

const toAbsoluteSpaceHref = (baseUrl: string, spacePathWithQuery: string) =>
  `${baseUrl}${spacePathWithQuery}`;

/**
 * Builds folder trail items for the header BreadCrumb on Workspace and form-edit pages.
 * Prefers folderIds/folderNames from the URL; falls back to Redux parentFolders on Workspace.
 */
export const useSpaceFolderBreadcrumbExtraItems = (): BreadCrumbItem[] => {
  const router = useRouter();
  const { publicRuntimeConfig } = getConfig();
  const parentFolders = useBreadcrumbFolders();

  return useMemo(() => {
    const menu = router.query.menu;
    const menuValue = Array.isArray(menu) ? menu[0] : menu;
    const isSpaceMenu = menuValue === 'space';
    const isFormEditPath = Boolean(router.pathname?.includes('/bricks/space/'));

    if (!isSpaceMenu && !isFormEditPath) {
      return [];
    }

    const baseUrl = (publicRuntimeConfig?.env?.APP_BASE_URL as string)?.replace(/\/$/, '') ?? '';
    const trailFromQuery = readSpaceFolderTrailFromQuery(router.query);

    if (trailFromQuery && trailFromQuery.folderNames.length > 0) {
      return trailFromQuery.folderNames.map((name, index) => {
        const isCurrentFolderOnSpace =
          isSpaceMenu && index === trailFromQuery.folderNames.length - 1;
        const folderId = trailFromQuery.folderIds[index];

        if (isCurrentFolderOnSpace || !folderId) {
          return { label: name };
        }

        return {
          label: name,
          href: toAbsoluteSpaceHref(
            baseUrl,
            buildSpaceUrl({
              folderId,
              folderIds: trailFromQuery.folderIds.slice(0, index + 1),
              folderNames: trailFromQuery.folderNames.slice(0, index + 1),
            }),
          ),
        };
      });
    }

    if (!isSpaceMenu) {
      return [];
    }

    const nestedFolders = parentFolders.filter((folder) => folder.id !== DEFAULT_FOLDER_ID);
    if (nestedFolders.length === 0) {
      return [];
    }

    return nestedFolders.map((folder, index) => {
      const isLast = index === nestedFolders.length - 1;
      if (isLast) {
        return { label: folder.title };
      }

      const folderIds = nestedFolders.slice(0, index + 1).map((item) => item.id);
      const folderNames = nestedFolders.slice(0, index + 1).map((item) => item.title);

      return {
        label: folder.title,
        href: toAbsoluteSpaceHref(
          baseUrl,
          buildSpaceUrl({
            folderId: folder.id,
            folderIds,
            folderNames,
          }),
        ),
      };
    });
  }, [router.query, router.pathname, parentFolders, publicRuntimeConfig?.env?.APP_BASE_URL]);
};
